// Activity Signals
//
// Records what the user does in the feeds into the append-only signal log
// (migration 045), which is the source of engagement truth we come back to for
// matching analysis.
//
// Signals are BATCHED. Impressions and dwell fire constantly while someone
// scrolls, and a request per card would put the feed's responsiveness at the
// mercy of the network. Everything queues and flushes on a timer, on a size
// threshold, or when the page is being hidden — whichever comes first.
//
// Tracking must never break the feed. Every failure path here is swallowed
// after logging: a lost signal costs a row in an analytics table, whereas a
// thrown error costs the user their scroll.

import { config } from '../../config/env';
import { authInterceptor } from '../Authentication/authInterceptor';
import { logger } from '../../utils/logger';

const API_BASE = `${config.api.baseUrl}/feed`;

/** Kept in step with activity_signal_types (migration 045). */
export type SignalType =
  | 'profile_like'
  | 'profile_pass'
  | 'deck_add'
  | 'deck_remove'
  | 'profile_open'
  | 'profile_dwell'
  | 'explore_tile_open'
  | 'photo_advance'
  | 'card_impression'
  | 'card_dwell'
  | 'quick_skip'
  | 'video_play'
  | 'video_complete'
  | 'video_replay'
  | 'moment_view'
  | 'moment_complete'
  | 'feed_open'
  | 'feed_tab_switch'
  | 'feed_scroll_depth'
  // Swipe deck (migration 046). Keeping and discarding a card are the same
  // acts as the heart and X in the feeds, so they record as profile_like /
  // profile_pass with surface='swipe' — see migration 050, which retired the
  // swipe-specific duplicates.
  | 'swipe_skip'
  | 'keep_pile_remove'
  | 'swipe_undo'
  // Messaging
  | 'message_send'
  | 'message_reply'
  | 'message_read'
  | 'conversation_open'
  | 'conversation_mute'
  | 'conversation_delete'
  // Chat rooms
  | 'post_open'
  | 'post_create'
  | 'comment_create'
  | 'post_upvote'
  | 'post_downvote'
  | 'comment_upvote'
  | 'comment_downvote'
  | 'post_save'
  | 'room_open'
  // Heart Games
  | 'game_start'
  | 'game_complete'
  | 'game_abandon'
  | 'compatibility_view'
  // Safety, from anywhere
  | 'user_block'
  | 'user_report';

export type SignalSurface =
  | 'matches' | 'nearby' | 'explore' | 'moments'
  | 'swipe' | 'profile' | 'messaging' | 'heart_games' | 'communities';

export interface Signal {
  signalType: SignalType;
  targetUserId?: string | null;
  surface?: SignalSurface;
  weight?: number;
  durationMs?: number;
  itemId?: string;
  metadata?: Record<string, unknown>;
}

interface QueuedSignal extends Signal {
  sessionId: string;
  occurredAt: string;
}

// Flush thresholds. 25 keeps a normal scrolling burst to a single request;
// 10s bounds how long a signal can sit unsent if the user goes idle mid-feed.
const MAX_BUFFER = 25;
const FLUSH_INTERVAL_MS = 10_000;
// The server rejects larger batches;; drop the oldest beyond this rather than
// grow without bound if flushes keep failing.
const MAX_QUEUE = 200;

/**
 * Below this, a card was scrolled past rather than looked at. Used to turn a
 * short dwell into a quick_skip, which is a genuine disinterest signal and
 * distinct from an explicit pass.
 */
export const QUICK_SKIP_THRESHOLD_MS = 1200;

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  // Enough for grouping a session; the server treats a malformed one as absent.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

class SignalsService {
  private queue: QueuedSignal[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  // One id for the life of the tab, so a burst of impressions can be told
  // apart from the same count spread over a week.
  private sessionId = uuid();
  private listenersBound = false;

  /** Queue a signal. Returns immediately; the network happens later. */
  track(signal: Signal): void {
    this.bindLifecycleListeners();

    this.queue.push({
      ...signal,
      sessionId: this.sessionId,
      occurredAt: new Date().toISOString(),
    });

    if (this.queue.length > MAX_QUEUE) {
      const dropped = this.queue.length - MAX_QUEUE;
      this.queue = this.queue.slice(-MAX_QUEUE);
      logger.log(`[SIGNALS] Queue full, dropped ${dropped} oldest signal(s)`);
    }

    if (this.queue.length >= MAX_BUFFER) {
      this.flush();
    } else if (!this.timer) {
      this.timer = setTimeout(() => this.flush(), FLUSH_INTERVAL_MS);
    }
  }

  /**
   * Dwell, expressed as a duration. Anything shorter than the quick-skip
   * threshold is recorded as disinterest instead — the same scroll produces a
   * different signal depending on how long it held the screen, which is the
   * whole point of measuring it.
   */
  trackDwell(
    signalType: Extract<SignalType, 'card_dwell' | 'profile_dwell'>,
    durationMs: number,
    signal: Omit<Signal, 'signalType' | 'durationMs'>,
  ): void {
    if (durationMs < QUICK_SKIP_THRESHOLD_MS && signalType === 'card_dwell') {
      this.track({ ...signal, signalType: 'quick_skip', durationMs });
      return;
    }
    // Weight scales with attention and saturates: 30 seconds on a profile is
    // meaningfully more than 3, but not ten times more.
    const saturated = Math.min(1, Math.log10(1 + durationMs / 1000) / 1.5);
    const base = signalType === 'profile_dwell' ? 0.5 : 0.3;
    this.track({ ...signal, signalType, durationMs, weight: Number((base * saturated).toFixed(3)) });
  }

  /**
   * Send what is queued. Safe to call at any time; a failed flush puts the
   * signals back so the next one can retry them.
   */
  async flush(): Promise<void> {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (!this.queue.length) return;

    const token = authInterceptor.getAccessToken();
    if (!token) {
      // Signed out mid-session: keep them queued rather than posting
      // unauthenticated and losing them.
      return;
    }

    const batch = this.queue;
    this.queue = [];

    try {
      const response = await fetch(`${API_BASE}/signals`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ signals: batch }),
        // Lets the request outlive the page during an unload flush.
        keepalive: true,
      });

      if (!response.ok) {
        // 4xx means this batch is malformed and retrying cannot help; dropping
        // it is better than a poison batch blocking every later signal.
        if (response.status >= 500) this.queue.unshift(...batch);
        logger.log(`[SIGNALS] Flush failed (${response.status})`);
        return;
      }

      const body = await response.json().catch(() => null);
      const rejected = body?.data?.rejected ?? [];
      if (rejected.length) {
        // Surfaced deliberately: a rejected signal means the client and the
        // catalogue disagree, which is a bug worth seeing rather than silence.
        logger.error('[SIGNALS] Server rejected signals:', rejected);
      }
    } catch (error) {
      this.queue.unshift(...batch);
      logger.log('[SIGNALS] Flush error, will retry:', error);
    }
  }

  /**
   * Flush on the way out. visibilitychange is the reliable one on mobile —
   * unload does not fire when an app is backgrounded or the tab is discarded.
   */
  private bindLifecycleListeners(): void {
    if (this.listenersBound || typeof document === 'undefined') return;
    this.listenersBound = true;

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flush();
    });
    window.addEventListener('pagehide', () => this.flush());
  }

  /** Engagement summary for the signed-in user — the read side of the log. */
  async getEngagementSummary(limit = 25): Promise<any> {
    const response = await fetch(`${API_BASE}/signals/summary?limit=${limit}`, {
      headers: { Authorization: `Bearer ${authInterceptor.getAccessToken() || ''}` },
    });
    if (!response.ok) throw new Error(`Failed to load engagement summary: ${response.status}`);
    const { data } = await response.json();
    return data;
  }
}

export const signalsService = new SignalsService();
